import React, { useMemo, useEffect, useRef, useState, useCallback, memo } from 'react';
import {
  IonCard,
  IonGrid,
  IonRow,
  IonCol,
  IonItem,
  IonLabel,
  IonText,
  IonIcon,
  IonButton,
  IonToolbar,
  IonButtons,
  IonTitle,
  IonCardHeader,
  IonChip,
  IonCardContent,
  IonRippleEffect,
  useIonRouter,
} from '@ionic/react';
import { format, addDays, isSameDay } from 'date-fns';
import { BookingItem, ProcessedBooking } from '@rsmaids/shared/types/bookings';
import styles from './ThreeDayView.module.scss';
import { useBusinessHours } from '../../hooks/useBusinessHours';
import { chevronBack, chevronDown, chevronForward } from 'ionicons/icons';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import { motion, useMotionValue, animate, useTransform } from 'motion/react';
import { formatTo12Hour } from '@rsmaids/shared/utils/timeUtils';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../../i18n/dynamic';
import { dfLocale } from '../../i18n/locale';

interface ThreeDayViewProps {
  currentDate: Date;
  bookings: BookingItem[];
  onDateChange: (date: Date) => void;
  onBookingSelect?: (booking: BookingItem) => void;
  completionStatuses?: Record<string, boolean>;
  onStatusChange?: (bookingId: string, isCompleted: boolean) => void;
  /** Tapping the month name in the header opens the month / year picker. */
  onMonthClick?: () => void;
  /** Ref for the month button, so the picker can find the calendar page it zooms. */
  monthButtonRef?: React.Ref<HTMLIonButtonElement>;
}

const calculateBookingHeight = (startTime: string | Date, endTime: string | Date): string => {
  let startHour: number, startMinute: number, endHour: number, endMinute: number;
  if (typeof startTime === 'string') {
    [startHour, startMinute] = startTime.split(':').map(Number);
  } else {
    startHour = startTime.getHours();
    startMinute = startTime.getMinutes();
  }
  if (typeof endTime === 'string') {
    [endHour, endMinute] = endTime.split(':').map(Number);
  } else {
    endHour = endTime.getHours();
    endMinute = endTime.getMinutes();
  }
  const totalStartMinutes = startHour * 60 + startMinute;
  const totalEndMinutes = endHour * 60 + endMinute;
  const durationInMinutes = totalEndMinutes - totalStartMinutes;
  return `${(durationInMinutes * (24 / 15))}px`;
};

const formatTimeToString = (time: string | Date): string => {
  if (typeof time === 'string') {
    return time;
  } else {
    return time.toTimeString().substring(0, 5);
  }
};

const AnimatedIonRow = motion.create(IonRow);

const ThreeDayView: React.FC<ThreeDayViewProps> = ({
  currentDate,
  bookings,
  onBookingSelect,
  onDateChange,
  completionStatuses = {},
  onMonthClick,
  monthButtonRef,
}) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLIonCardElement>(null);
  const startXRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isSwipingRef = useRef(false);
  const isVerticalScrollingRef = useRef(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const x = useMotionValue(0);
  const xTransform = useTransform(x, (value: number) => `translateX(calc(-33.333% + ${value}px))`);
  const animatedStyle = {
    transform: xTransform,
    willChange: 'transform',
    backfaceVisibility: 'hidden' as const,
    perspective: 1000
  };
  const [currentTime, setCurrentTime] = useState(new Date());
  const timeIndicatorRef = useRef<HTMLIonRowElement>(null);
  const timeGridRef = useRef<HTMLIonGridElement>(null);
  useEffect(() => {
    const updateCurrentTime = () => setCurrentTime(new Date());
    updateCurrentTime();
    const intervalId = setInterval(updateCurrentTime, 60000);
    return () => clearInterval(intervalId);
  }, []);
  const currentTimePosition = useMemo(() => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const totalMinutes = (hours * 60 + minutes) + 15;
    return totalMinutes * (24 / 15);
  }, [currentTime]);
  // The grid follows the current time only during business hours (today's real opening
  // hours from the business profile). Outside them it sits with opening time at the top.
  const { openMinutes, closeMinutes } = useBusinessHours(currentTime);
  useEffect(() => {
    const ROW_PX = 24; // one 15-minute row
    const businessStartPosition = (openMinutes / 15) * ROW_PX;
    const nowMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();
    const inBusinessHours = nowMinutes >= openMinutes && nowMinutes < closeMinutes;
    const top = inBusinessHours
      ? Math.max(businessStartPosition, currentTimePosition - 200)
      : businessStartPosition;

    // Applied now and again once the view has finished laying out / animating in: right
    // after switching views the grid can still be settling, which made a single early
    // assignment get lost. behavior 'auto' = jump (the grid's CSS default is smooth).
    const apply = () => {
      const timeGrid = timeIndicatorRef.current?.closest(`.${styles.timeGrid}`);
      timeGrid?.scrollTo({ top, behavior: 'auto' });
    };
    apply();
    const frame = requestAnimationFrame(apply);
    const settle = setTimeout(apply, 350);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(settle);
    };
  }, [currentTimePosition, currentTime, openMinutes, closeMinutes]);
  const safeCurrentDate = useMemo(() => {
    const date = currentDate || new Date();
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, [currentDate]);
  const { prevDays, currentDays, nextDays } = useMemo(() => ({
    prevDays: Array.from({ length: 3 }, (_, i) => addDays(safeCurrentDate, i - 3)),
    currentDays: Array.from({ length: 3 }, (_, i) => addDays(safeCurrentDate, i)),
    nextDays: Array.from({ length: 3 }, (_, i) => addDays(safeCurrentDate, i + 3))
  }), [safeCurrentDate]);
  const timeSlots = useMemo(() => {
    const slots = [];
    const prevDay = addDays(safeCurrentDate, -1);
    slots.push({
      hour: 23,
      minute: 45,
      isHourStart: false,
      isHalfHour: false,
      isQuarterHour: true,
      displayHour: '',
      key: '23-45',
      isMidnight: false,
      isLastHour: false,
      date: prevDay
    });
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 23 && minute === 45) continue;
        slots.push({
          hour,
          minute,
          isHourStart: minute === 0,
          isHalfHour: minute === 30,
          isQuarterHour: minute === 15 || minute === 45,
          displayHour: minute === 0 ?
            (hour === 0 ? '12AM' :
              hour < 12 ? `${hour}AM` :
                hour === 12 ? '12PM' :
                  `${hour - 12}PM`) : '',
          key: `${hour}-${minute}`,
          isMidnight: hour === 0 && minute === 0,
          isLastHour: hour === 23,
          date: safeCurrentDate
        });
      }
    }
    return slots;
  }, [safeCurrentDate]);
  const [isAnimating, setIsAnimating] = useState(false);
  const handleScroll = useCallback(() => {
    if (!isSwipingRef.current) {
      isVerticalScrollingRef.current = true;
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        isVerticalScrollingRef.current = false;
      }, 200);
    }
  }, []);
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isAnimating) return;
    startXRef.current = e.touches[0].clientX;
    const isTimeGrid = e.currentTarget === timeGridRef.current;
    if (!isTimeGrid) {
      isDraggingRef.current = true;
      isSwipingRef.current = false;
    }
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    if (isAnimating) return;
    const currentX = e.touches[0].clientX;
    const diffX = currentX - startXRef.current;
    if (Math.abs(diffX) > 15 && !isVerticalScrollingRef.current) {
      isDraggingRef.current = true;
      isSwipingRef.current = true;
      x.set(diffX);
    } else if (isDraggingRef.current && isSwipingRef.current) {
      x.set(diffX);
    }
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isAnimating) {
      isDraggingRef.current = false;
      isSwipingRef.current = false;
      return;
    }
    if (isDraggingRef.current && isSwipingRef.current) {
      const currentX = e.changedTouches[0].clientX;
      const diffX = currentX - startXRef.current;
      const threshold = 50;
      if (Math.abs(diffX) > threshold) {
        const newDate = addDays(safeCurrentDate, diffX > 0 ? -3 : 3);
        setIsAnimating(true);
        setPendingMonthDate(newDate);
        const cardWidth = cardRef.current?.offsetWidth || 0;
        const timeColWidth = cardRef.current?.querySelector(`.${styles.timeCol}`)?.clientWidth || 60;
        const availableWidth = cardWidth - timeColWidth;
        const moveX = diffX > 0 ? availableWidth : -availableWidth;
        animate(x, moveX, {
          type: "spring",
          stiffness: 220,
          damping: 24
        }).then(() => {
          onDateChange(newDate);
          x.set(0);
          setIsAnimating(false);
        });
      } else {
        animate(x, 0, {
          type: "spring",
          stiffness: 220,
          damping: 24
        }).then(() => {
          x.set(0);
          setIsAnimating(false);
        });
      }
    }
    isDraggingRef.current = false;
    isSwipingRef.current = false;
  };
  const handleHorizontalScroll = (direction: 'left' | 'right') => {
    setIsAnimating(true);
    const newDate = addDays(safeCurrentDate, direction === 'left' ? -3 : 3);
    setPendingMonthDate(newDate);
    const cardWidth = cardRef.current?.offsetWidth || 0;
    const timeColWidth = cardRef.current?.querySelector(`.${styles.timeCol}`)?.clientWidth || 60;
    const availableWidth = cardWidth - timeColWidth;
    const moveX = direction === 'left' ? availableWidth : -availableWidth;
    animate(x, moveX, {
      type: "spring",
      stiffness: 220,
      damping: 24
    }).then(() => {
      onDateChange(newDate);
      x.set(0);
      setIsAnimating(false);
    });
  };
  const handlePrevious = () => handleHorizontalScroll('left');
  const handleNext = () => handleHorizontalScroll('right');
  const bookingCache = useRef<Record<string, ProcessedBooking[]>>({});
  useEffect(() => {
    bookingCache.current = {};
  }, [bookings]);
  function toDate(val: string | Date): Date {
    if (val instanceof Date) return val;
    return new Date(val);
  }
  const visibleBookings = useMemo(
    () => bookings.filter(b => {
      const status = String(b.status).toLowerCase();
      return status !== 'canceled' && status !== 'cancelled';
    }),
    [bookings]
  );
  const getBookingsForSlot = useCallback((
    date: Date,
    slot: { hour: number, minute: number, key: string }
  ): ProcessedBooking[] => {
    const slotStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), slot.hour, slot.minute, 0, 0);
    const slotEnd = new Date(slotStart.getTime() + 15 * 60 * 1000);
    return visibleBookings
      .filter(booking => {
        const bookingStart = toDate(booking.startTime);
        return bookingStart >= slotStart && bookingStart < slotEnd;
      })
      .sort((a, b) => toDate(a.startTime).getTime() - toDate(b.startTime).getTime())
      .map((booking, index, arr) => ({
        ...booking,
        columnIndex: index,
        totalColumns: arr.length
      }));
  }, [visibleBookings]);
  const BookingItem = memo(({
    booking,
    onSelect
  }: {
    booking: ProcessedBooking,
    onSelect?: (booking: BookingItem) => void
  }) => {
  const { t } = useTranslation();
    const ionRouter = useIonRouter();
    const isCompleted = completionStatuses[booking.id] || booking.status === 'completed';
    const handleCardClick = useCallback((event: React.MouseEvent) => {
      event.stopPropagation();
      if (onSelect) {
        onSelect(booking);
        return;
      }
      DataStorageService.storeData('selectedBooking', booking, false);
      ionRouter.push(`/bookingSystem/booking/${booking.id}`, 'forward', 'push');
    }, [booking, onSelect, ionRouter]);
    const [isPressed, setIsPressed] = useState(false);
    const rippleRef = useRef<HTMLIonRippleEffectElement>(null);
    const isSingleColumn = booking.totalColumns === 1;
    const columnClass = isSingleColumn ? styles.singleColumn : styles.multiColumn;
    const serviceColor = serviceColorMap[booking.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)';
    const height = calculateBookingHeight(booking.startTime, booking.endTime);
    const isShortSlot = height === '24px' || height === '16px' || parseInt(height) <= 24;
    const cssVars = {
      ...(isSingleColumn
        ? {}
        : {
          '--booking-left': `calc(${(booking.columnIndex * 100) / booking.totalColumns}% + 2px)`,
          '--booking-width': `calc(${100 / booking.totalColumns}% - 4px)`
        }),
      '--booking-height': height,
      '--booking-bg-color': serviceColor,
      transform: isPressed ? 'translateZ(0) scale(0.97)' : 'translateZ(0) scale(1)',
      transition: 'transform 150ms'
    } as React.CSSProperties;
    const handleTouchStart = (event: React.TouchEvent) => {
      setIsPressed(true);
      if (rippleRef.current) {
        const x = event.touches[0].clientX;
        const y = event.touches[0].clientY;
        rippleRef.current.addRipple(x, y);
      }
    };
    const handleMouseDown = (event: React.MouseEvent) => {
      setIsPressed(true);
      if (rippleRef.current) {
        const x = event.clientX;
        const y = event.clientY;
        rippleRef.current.addRipple(x, y);
      }
    };
    const handleTouchEnd = () => {
      setIsPressed(false);
    };
    const isBlocked = booking.type === 'blocked';
    const isOpaque = isBlocked && booking.transparency === 'OPAQUE';
    if (isBlocked) {
      const blockedLabel = translateDynamic(booking.agendaItem?.title || 'Blocked Time');
      const isSingleColumn = booking.totalColumns === 1;
      const columnClass = isSingleColumn ? styles.singleColumn : styles.multiColumn;
      return (
        <IonCard
          className={`${styles.bookingCard} ${styles.blockedSlot} ${columnClass} ${isOpaque ? styles.opaque : ''}`}
          style={cssVars}
          onClick={handleCardClick}
          button
        >
          <IonCardContent className={styles.bookingContent}>
            <IonText className={styles.clientName}>{blockedLabel}</IonText>
          </IonCardContent>
        </IonCard>
      );
    }
    return (
      <IonCard
        key={booking.id}
        className={`${styles.bookingCard} ${isShortSlot ? styles.shortSlot : ''} ${columnClass} ${isCompleted ? styles.completed : ''}`}
        onClick={handleCardClick}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseUp={handleTouchEnd}
        onMouseLeave={handleTouchEnd}
        button={false}
        style={cssVars}
        data-booking-id={booking.id}
      >
        <IonRippleEffect ref={rippleRef} />
        <IonCardContent className={styles.bookingContent}>
          <IonText className={styles.bookingName}>
            {booking.contact?.name || booking.title || t('pages.bookingCalendar.ThreeDayView.unnamed')}
          </IonText>
          <IonText className={styles.bookingService}>
            {translateDynamic(booking.serviceType || '')}
          </IonText>
          <IonText className={styles.bookingTime}>
            {formatTo12Hour(formatTimeToString(booking.startTime))} - {formatTo12Hour(formatTimeToString(booking.endTime))}
          </IonText>
          {isCompleted && (
            <IonText className={styles.completedIndicator}>✓</IonText>
          )}
        </IonCardContent>
      </IonCard>
    );
  });
  const [pendingMonthDate, setPendingMonthDate] = useState(currentDate);
  useEffect(() => {
    setPendingMonthDate(currentDate);
  }, [currentDate]);
  return (
    <IonCard
      ref={cardRef}
      className={styles.threeDayView}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        touchAction: isAnimating ? "none" : "pan-y",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <IonCardHeader className={styles.headerCard}>
        <IonToolbar>
          <IonTitle>
            <IonButton
              ref={monthButtonRef}
              fill="clear"
              className={styles.monthButton}
              aria-label={t('pages.bookingCalendar.ThreeDayView.chooseMonthAndYear')}
              onClick={onMonthClick}
            >
              {format(pendingMonthDate, 'MMMM yyyy', { locale: dfLocale() })}
              <IonIcon icon={chevronDown} slot="end" />
            </IonButton>
          </IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" aria-label={t('pages.bookingCalendar.ThreeDayView.previousPeriod')} onClick={handlePrevious}>
              <IonIcon icon={chevronBack} />
            </IonButton>
            <IonButton fill="clear" aria-label={t('pages.bookingCalendar.ThreeDayView.nextPeriod')} onClick={handleNext}>
              <IonIcon icon={chevronForward} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonCardHeader>
      <IonGrid className={styles.headerGrid}>
        <IonRow>
          <IonCol size="2" className={styles.timeCol}>{t('pages.bookingCalendar.ThreeDayView.time')}
          </IonCol>
          <IonCol className={styles.dayHeadersWrapper}>
            <AnimatedIonRow
              className={styles.allDayHeaders}
              style={animatedStyle}
            >
              {prevDays.map(date => (
                <IonCol key={date.toString()} className={styles.dayCol}>
                  <IonItem lines="none" className={styles.dayHeader}>
                    <IonLabel>
                      <IonText className={styles.dayName}>
                        {format(date, 'EEE', { locale: dfLocale() })}
                      </IonText>
                      <IonText
                        className={`${styles.dayNumber} 
                          ${isSameDay(date, new Date()) ? styles.today : ''}`}
                      >
                        {format(date, 'd')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              ))}
              {currentDays.map(date => (
                <IonCol key={date.toString()} className={styles.dayCol}>
                  <IonItem lines="none" className={styles.dayHeader}>
                    <IonLabel>
                      <IonText className={styles.dayName}>
                        {format(date, 'EEE', { locale: dfLocale() })}
                      </IonText>
                      <IonText
                        className={`${styles.dayNumber} 
                          ${isSameDay(date, new Date()) ? styles.today : ''}`}
                      >
                        {format(date, 'd')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              ))}
              {nextDays.map(date => (
                <IonCol key={date.toString()} className={styles.dayCol}>
                  <IonItem lines="none" className={styles.dayHeader}>
                    <IonLabel>
                      <IonText className={styles.dayName}>
                        {format(date, 'EEE', { locale: dfLocale() })}
                      </IonText>
                      <IonText
                        className={`${styles.dayNumber} 
                          ${isSameDay(date, new Date()) ? styles.today : ''}`}
                      >
                        {format(date, 'd')}
                      </IonText>
                    </IonLabel>
                  </IonItem>
                </IonCol>
              ))}
            </AnimatedIonRow>
          </IonCol>
        </IonRow>
      </IonGrid>
      <IonGrid className={styles.timeGrid} ref={timeGridRef} onScroll={handleScroll}>
          <IonRow className={styles.backgroundGridLayer}>
            <IonCol size="2" className={styles.timeColSpacer}></IonCol>
            <IonCol className={styles.gridLinesWrapper}>
              <AnimatedIonRow
                className={styles.animatedGridLines}
                style={{
                  transform: xTransform,
                }}
              >
                {[...Array(10)].map((_, i) => {
                  if (i === 0) {
                    return (
                      <IonCol
                        key="grid-col-spacer"
                        className={styles.gridColSpacer}
                      ></IonCol>
                    );
                  }
                  return (
                    <IonCol
                      key={`grid-col-${i}`}
                      className={styles.gridCol}
                    >
                      {timeSlots.map(slot => (
                        <IonRow
                          key={`grid-${i}-${slot.key}`}
                          className={`${styles.gridRow} 
                          ${slot.isHourStart ? styles.hourStart : ''}
                          ${slot.isHalfHour ? styles.halfHour : ''}
                          ${slot.isQuarterHour ? styles.quarterHour : ''}
                          ${((slot.hour === 23 && slot.minute === 45) || (slot.hour >= 0 && slot.hour < 6) || (slot.hour >= 18 && slot.hour <= 23)) ? styles.offHours : ''}`}
                        >
                        </IonRow>
                      ))}
                    </IonCol>
                  );
                })}
              </AnimatedIonRow>
            </IonCol>
          </IonRow>
        <IonRow
          className={styles.currentTimeOverlay}
          style={{
            top: `${currentTimePosition}px`,
            transform: 'translateY(-50%)'
          }}
          ref={timeIndicatorRef}
        >
          <IonCol size="2" className={styles.timeTextCol}>
            <IonText color="primary" className={styles.currentTimeText}>
              {format(currentTime, 'h:mm a', { locale: dfLocale() })}
            </IonText>
          </IonCol>
          <IonCol className={styles.lineContainer}>
            <IonChip color="primary" className={styles.timeIndicatorDot}></IonChip>
            <IonItem lines="none" className={styles.timeIndicatorLine} />
          </IonCol>
        </IonRow>
        <IonRow className={styles.timeGridContent}>
          <IonCol size="2" className={styles.timeColumnContainer}>
            <IonGrid className={styles.timeLabelsGrid}>
              {timeSlots.map(slot => {
                const now = currentTime;
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                const isCloseToCurrentTime =
                  slot.isHourStart && (
                    (slot.hour === currentHour + 1 && currentMinute >= 52) ||
                    (slot.hour === currentHour && currentMinute <= 8)
                  );
                return (
                  <IonRow key={`time-${slot.key}`} className={styles.timeLabelRow}>
                    <IonCol className={styles.timeLabelCol}>
                      {slot.isHourStart && !isCloseToCurrentTime && (
                        <IonText className={styles.timeLabel}>
                          {slot.displayHour}
                        </IonText>
                      )}
                    </IonCol>
                  </IonRow>
                );
              })}
            </IonGrid>
          </IonCol>
          <IonCol className={styles.dayGridContainer}>
            <IonGrid className={styles.dayGrid}>
              {timeSlots.map((slot) => (
                <IonRow
                  key={slot.key}
                  className={styles.timeRow}
                >
                  <IonCol className={styles.dayColumnsWrapper}>
                    <AnimatedIonRow
                      className={styles.allDayColumns}
                      style={{
                        transform: xTransform,
                      }}
                    >
                      {prevDays.map(date => (
                        <IonCol
                          key={`${date.toString()}-${slot.key}`}
                          className={styles.dayCol}
                        >
                          {getBookingsForSlot(date, slot).map((booking: ProcessedBooking) => (
                            <BookingItem
                              key={booking.id}
                              booking={booking}
                              onSelect={onBookingSelect}
                            />
                          ))}
                        </IonCol>
                      ))}
                      {currentDays.map(date => (
                        <IonCol
                          key={`${date.toString()}-${slot.key}`}
                          className={styles.dayCol}
                        >
                          {getBookingsForSlot(date, slot).map((booking: ProcessedBooking) => (
                            <BookingItem
                              key={booking.id}
                              booking={booking}
                              onSelect={onBookingSelect}
                            />
                          ))}
                        </IonCol>
                      ))}
                      {nextDays.map(date => (
                        <IonCol
                          key={`${date.toString()}-${slot.key}`}
                          className={styles.dayCol}
                        >
                          {getBookingsForSlot(date, slot).map((booking: ProcessedBooking) => (
                            <BookingItem
                              key={booking.id}
                              booking={booking}
                              onSelect={onBookingSelect}
                            />
                          ))}
                        </IonCol>
                      ))}
                    </AnimatedIonRow>
                  </IonCol>
                </IonRow>
              ))}
            </IonGrid>
          </IonCol>
        </IonRow>
      </IonGrid>
    </IonCard>
  );
};

export default React.memo(ThreeDayView, (prevProps, nextProps) => {
  return prevProps.currentDate.getTime() === nextProps.currentDate.getTime() &&
    prevProps.bookings === nextProps.bookings &&
    prevProps.onBookingSelect === nextProps.onBookingSelect &&
    prevProps.onDateChange === nextProps.onDateChange &&
    prevProps.completionStatuses === nextProps.completionStatuses &&
    prevProps.onStatusChange === nextProps.onStatusChange &&
    prevProps.onMonthClick === nextProps.onMonthClick &&
    prevProps.monthButtonRef === nextProps.monthButtonRef;
});
